import React from 'react'
import {
  Card,
  Group,
  Stack,
  Text,
  Title,
  Button,
  Divider,
  Box,
  SimpleGrid,
  Loader,
} from '@mantine/core'
import { IconCalendar, IconClock, IconUser, IconArrowLeft, IconCheck } from '@tabler/icons-react'

export interface ConfirmAppointmentCardProps {
  dateLabel?: string // optional: "2026-01-07" or shown in badge
  timeLabel: string // e.g. "14:00"
  patientName: string // e.g. "John Smith"
  onConfirm: () => void
  onBack?: () => void
  confirmLabel?: string // default "Confirm & book"
  loading?: boolean
  disabled?: boolean
}

export const ConfirmAppointmentCard: React.FC<ConfirmAppointmentCardProps> = ({
  dateLabel,
  timeLabel,
  patientName,
  onConfirm,
  onBack,
  confirmLabel = 'Confirm & book',
  loading = false,
  disabled = false,
}) => {
  return (
    <Card
      radius="xl"
      p="xl"
      withBorder
      style={{
        width: 460,
        margin: '0 auto',
        borderColor: 'rgba(34, 139, 230, 0.18)',
        background:
          'radial-gradient(1200px 600px at 20% -10%, rgba(34,139,230,0.14) 0%, transparent 55%),' +
          'radial-gradient(900px 500px at 110% 10%, rgba(99,102,241,0.10) 0%, transparent 55%),' +
          'linear-gradient(180deg, rgba(255,255,255,0.92) 0%, rgba(255,255,255,0.98) 100%)',
        boxShadow: '0 20px 45px rgba(0,0,0,0.08), 0 2px 10px rgba(0,0,0,0.05)',
      }}
    >
      <Group justify="space-between" align="flex-start">
        <Stack gap={4}>
          <Title order={2} style={{ lineHeight: 1.1 }}>
            Confirm appointment
          </Title>
          <Text c="dimmed" size="sm">
            Please review the details before booking.
          </Text>
        </Stack>
      </Group>

      <Divider my="lg" />

      {/* Summary */}
      <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="lg" mb="lg">
        <InfoTile icon={<IconCalendar size={30} />} label="Date" value={dateLabel as string} />
        <InfoTile icon={<IconClock size={30} />} label="Time" value={timeLabel} />
      </SimpleGrid>
      <InfoTile icon={<IconUser size={30} />} label="Patient" value={patientName} />

      <Divider my="lg" />

      {/* Footer actions */}
      <Group justify="space-between" align="center">
        <Button
          variant="outline"
          leftSection={<IconArrowLeft size={16} />}
          onClick={onBack}
          disabled={!onBack || loading}
        >
          Back
        </Button>
        <Button
          leftSection={!loading && <IconCheck size={16} />}
          onClick={onConfirm}
          loading={loading}
          disabled={disabled || loading}
        >
          {loading ? <Loader size={20} color="rgb(49, 109, 171)" /> : confirmLabel}
        </Button>
      </Group>
    </Card>
  )
}

interface InfoTileProps {
  icon: React.ReactNode
  label: string
  value: string
  secondary?: string
}

const InfoTile: React.FC<InfoTileProps> = ({ icon, label, value, secondary }) => {
  return (
    <Card
      radius="lg"
      p="md"
      withBorder
      style={{
        background: 'rgba(255,255,255,0.6)',
        borderColor: 'rgba(0,0,0,0.06)',
      }}
    >
      <Group justify="space-between" align="flex-start" wrap="nowrap">
        <Group gap="sm" wrap="nowrap" style={{ minWidth: 0 }}>
          <Box
            style={{
              width: 48,
              height: 48,
              borderRadius: 12,
              display: 'grid',
              placeItems: 'center',
              background: 'rgba(34, 139, 230, 0.10)',
              border: '1px solid rgba(34, 139, 230, 0.18)',
              flex: '0 0 auto',
            }}
          >
            {icon}
          </Box>

          <Stack gap={2} style={{ minWidth: 0 }}>
            <Text size="xs" c="dimmed" style={{ textAlign: 'left' }}>
              {label}
            </Text>

            <Text fw={650} style={{ wordBreak: 'break-word' }}>
              {value}{' '}
              {secondary ? (
                <Text component="span" c="dimmed" fw={500}>
                  {secondary}
                </Text>
              ) : null}
            </Text>
          </Stack>
        </Group>
      </Group>
    </Card>
  )
}
